import React, { FC, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Card } from '@components/atoms/Card';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import styles from './ProductCard.module.css';
import { formatPrice } from '@utils/format';
import { displayOldPrice, displayPrice, displayPromotion } from '@utils/products';
import { SITE_URLS } from '@constants';
import { Modules, useModule } from '@hooks/useModule';
import { useAddFavoriteProduct } from '@services/useAddFavoriteProduct';
import { useRemoveFavoriteProduct } from '@services/useRemoveFavoriteProduct';
import { useThemes } from '@hooks/useThemes';

export interface ProductCardProps {
    uuid?: string;
    merchant?: string;
    name?: string;
    image?: string;
    price?: number;
    oldPrice?: number;
    initialFavorite: boolean;
    promo?: number | string; // #TODO: format will be updated on BE
    truncateTitle?: boolean;
    titleTagVariant?: TypographyTagVariants;
}

export const ProductCard: FC<ProductCardProps> = memo(
    ({ uuid, merchant, name, image, price, oldPrice, initialFavorite, promo, truncateTitle, titleTagVariant }) => {
        const { useGrayscaleTheme } = useThemes();
        const { isModuleActive } = useModule();
        const {
            request: requestAddFavorite,
            loading: loadingAddFavorite,
            error: errorAddFavorite,
            lastResponseTime: lastAddFavorite,
        } = useAddFavoriteProduct();
        const {
            request: requestRemoveFavorite,
            loading: loadingRemoveFavorite,
            error: errorRemoveFavorite,
            lastResponseTime: lastRemoveFavorite,
        } = useRemoveFavoriteProduct();
        const { t } = useTranslation();

        const translations = {
            priceOnRequest: t('price-on-request'),
        };

        const [favorite, setFavorite] = useState<boolean>(initialFavorite || false);

        let percentage = 0;
        if (promo && oldPrice) {
            percentage = Math.round((Number(promo) / oldPrice) * 100);
        }

        const handleOnClickFavorite = () => {
            if (!loadingAddFavorite && !loadingRemoveFavorite) {
                favorite ? requestRemoveFavorite({ uuid: uuid || '' }) : requestAddFavorite({ uuid: uuid || '' });
            }
        };

        useEffect(() => {
            if (lastAddFavorite && !errorAddFavorite && !favorite) {
                setFavorite(true);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [lastAddFavorite]);

        useEffect(() => {
            if (lastRemoveFavorite && !errorRemoveFavorite && favorite) {
                setFavorite(false);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [lastRemoveFavorite]);

        return (
            <Card classes={`${useGrayscaleTheme ? styles['theme-grayscale'] : ''} ${styles['product-card']}`}>
                <div className={`${styles.overlay} d-flex justify-content-between`}>
                    {/* promo percentage */}
                    {displayPromotion(promo) ? (
                        <div className={`${styles.promo} align-content-center text-center bg-dark`}>
                            <Typography tagVariant={TypographyTagVariants.span} fontSize="12">
                                <>{`${percentage ?? ''}%`}</>
                            </Typography>
                        </div>
                    ) : (
                        <div className={styles.promo}></div>
                    )}

                    {/* set/unset as favorite */}
                    {isModuleActive(Modules.favorite) && (
                        <RoundIcon
                            icon={`${favorite ? 'fa-solid' : 'fa-light'} fa-heart`}
                            variant={RoundIconVariants.variant3}
                            onClick={handleOnClickFavorite}
                        />
                    )}
                </div>
                <Anchor
                    href={SITE_URLS.productDetail.replace(':uuid', `${uuid}`)}
                    classes="text-decoration-none"
                    mode={AnchorMode.none}>
                    <>
                        <div className={`position-relative ${styles['card-sizing']}`}>
                            <Card.ImageOverlay />
                            <img src={image} className="card-img" alt={name} />
                        </div>
                        <div className="card-body px-0">
                            <Card.Title
                                typography={{
                                    tagVariant: titleTagVariant ?? TypographyTagVariants.h2,
                                    classes: `${styles.title} ${truncateTitle && styles['truncate-title']}`,
                                    color: 'dark',
                                    fontWeight: '900',
                                    fontSize: '16',
                                    children: name,
                                }}
                                underlineAnimation={true}
                            />
                            <Typography classes="text-truncate my-1" color="muted" fontSize="14">
                                {merchant}
                            </Typography>
                            {displayOldPrice(oldPrice, price) && (
                                <Typography classes={`${styles.price} text-decoration-line-through me-2`} fontSize="16">
                                    {formatPrice(oldPrice)}
                                </Typography>
                            )}
                            <Typography classes={styles.price} fontWeight="900" fontSize="16">
                                {displayPrice(price) ? formatPrice(price) : translations.priceOnRequest}
                            </Typography>
                        </div>
                    </>
                </Anchor>
            </Card>
        );
    }
);

ProductCard.displayName = 'ProductCard';
